import { useState, useEffect, FormEvent } from 'react';
import styled from 'styled-components';
import colors from '../../../../styles/colors';
import {
  apiUpdateExpansion,
  apiAttachCard,
  apiDetachCard,
  apiGetExpansion,
  apiUpdateCardDropRate,
} from '../../../../api/expansionApi';
import { apiGetCards } from '../../../../api/cardApi';
import type { Expansion, ExpansionCardItem } from '../../../../types/models/expansion';
import type { Card } from '../../../../types/models/card';

// ─── Styled Components — Struttura ────────────────────────────────────────────

const Wrapper = styled.div`
  border: 1px solid rgba(255, 215, 0, 0.25);
  border-top: none;
  border-radius: 0 0 6px 6px;
  background-color: #161616;
  margin-bottom: 8px;
`;

const TabBar = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 10px 18px;
  background-color: ${({ active }) => (active ? '#1e1e1e' : 'transparent')};
  color: ${({ active }) => (active ? 'gold' : 'rgba(255,255,255,0.45)')};
  border: none;
  border-bottom: 2px solid ${({ active }) => (active ? 'gold' : 'transparent')};
  font-size: 13px;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  cursor: pointer;
  transition: color 0.15s;
  &:hover { color: gold; }
`;

const TabContent = styled.div`
  padding: 20px 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  color: gold;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const Input = styled.input`
  display: block;
  width: 100%;
  padding: 7px 10px;
  font-size: 14px;
  color: #fff;
  background-color: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  box-sizing: border-box;
  &:focus { outline: none; border-color: gold; }
  &:disabled { opacity: 0.4; }
`;

const Textarea = styled.textarea`
  display: block;
  width: 100%;
  padding: 7px 10px;
  font-size: 14px;
  color: #fff;
  background-color: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  box-sizing: border-box;
  min-height: 60px;
  resize: vertical;
  &:focus { outline: none; border-color: gold; }
`;

const ToggleRow = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
`;

const ToggleBtn = styled.button<{ active: boolean }>`
  padding: 3px 10px;
  border-radius: 4px;
  border: 1px solid ${({ active }) => (active ? 'gold' : 'rgba(255,255,255,0.15)')};
  background-color: ${({ active }) => (active ? 'rgba(255,215,0,0.12)' : 'transparent')};
  color: ${({ active }) => (active ? 'gold' : 'rgba(255,255,255,0.4)')};
  font-size: 12px;
  cursor: pointer;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  cursor: pointer;
`;

const SaveBtn = styled.button`
  padding: 8px 20px;
  background-color: ${colors.bgColorBordeaux};
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 4px;
  &:hover:not(:disabled) { background-color: ${colors.bgColorRed}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ErrorMsg = styled.p`
  color: #dc3545;
  font-size: 13px;
  margin: 6px 0 0;
`;

const SuccessMsg = styled.p`
  color: #28a745;
  font-size: 13px;
  margin: 6px 0 0;
`;

const EmptyMsg = styled.p`
  color: rgba(255, 255, 255, 0.35);
  font-size: 13px;
  margin: 0;
`;

const LoadingText = styled.p`
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
  margin: 12px 0;
`;

// ─── Styled Components — Inserimento Carte ────────────────────────────────────

const FilterInput = styled(Input)`
  margin-bottom: 12px;
`;

const CardPickGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  align-items: start;
`;

// Outer wrapper — NO overflow:hidden, NO aspect-ratio
const CardPickItem = styled.div<{ attached: boolean; selected: boolean }>`
  border-radius: 6px;
  border: 2px solid
    ${({ selected, attached }) =>
      selected ? 'gold' : attached ? 'rgba(40, 167, 69, 0.8)' : 'rgba(255,255,255,0.06)'};
  background-color: #222;
  transition: border-color 0.12s;
  overflow: hidden;
  cursor: ${({ attached }) => (attached ? 'default' : 'pointer')};
  &:hover {
    border-color: ${({ selected, attached }) =>
      selected ? 'gold' : attached ? 'rgba(40, 167, 69, 0.8)' : 'rgba(255, 215, 0, 0.55)'};
  }
`;

// Image area — aspect-ratio + overflow:hidden qui
const CardPickImgWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 2 / 3;
  overflow: hidden;
  background-color: #2a2a2a;
`;

const CardPickImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CardPickPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.18);
  font-size: 10px;
  text-align: center;
  padding: 4px;
`;

const CardPickAttachedOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(40, 167, 69, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  pointer-events: none;
`;

const CardPickIdLabel = styled.div`
  padding: 3px 6px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 10px;
  text-align: center;
  background-color: #1a1a1a;
`;

// Panel espanso quando la carta è selezionata
const CardPickExpandedPanel = styled.div`
  padding: 8px 6px;
  background-color: rgba(255, 215, 0, 0.06);
  border-top: 1px solid rgba(255, 215, 0, 0.25);
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const DropRateLabel = styled.div`
  color: gold;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const DropRatePickInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 5px 7px;
  font-size: 13px;
  color: #fff;
  background-color: #2a2a2a;
  border: 1px solid rgba(255, 215, 0, 0.4);
  border-radius: 4px;
  &:focus { outline: none; border-color: gold; }
`;

const AddCardBtn = styled.button`
  width: 100%;
  padding: 6px;
  background-color: ${colors.bgColorBordeaux};
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { background-color: ${colors.bgColorRed}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// Bottone rimuovi — visibile sotto la carta associata
const RemoveCardBtn = styled.button`
  width: 100%;
  padding: 6px;
  background-color: rgba(220, 53, 69, 0.12);
  color: #dc3545;
  border: none;
  border-top: 1px solid rgba(220, 53, 69, 0.3);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.2px;
  &:hover:not(:disabled) { background-color: rgba(220, 53, 69, 0.25); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

// ─── Styled Components — Drop Rate ────────────────────────────────────────────

const DropRateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  align-items: start;
`;

const DropRateCard = styled.div`
  background-color: #222;
  border-radius: 6px;
  overflow: hidden;
`;

const DropRateCardImgWrapper = styled.div`
  width: 100%;
  aspect-ratio: 2 / 3;
  background-color: #2a2a2a;
  overflow: hidden;
`;

const DropRateCardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const DropRateCardImgPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.2);
  font-size: 11px;
`;

const DropRateCardBody = styled.div`
  padding: 8px;
`;

const DropRateCardId = styled.div`
  color: rgba(255, 255, 255, 0.55);
  font-size: 11px;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DropRateCardControls = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;

const DropRateInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 5px 7px;
  font-size: 13px;
  color: #fff;
  background-color: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  &:focus { outline: none; border-color: gold; }
  &:disabled { opacity: 0.4; }
`;

const UpdateBtn = styled.button`
  padding: 5px 10px;
  background-color: transparent;
  color: gold;
  border: 1px solid gold;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  &:hover { background-color: rgba(255, 215, 0, 0.1); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const InlineError = styled.div`
  color: #dc3545;
  font-size: 11px;
  margin-top: 4px;
`;

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExpFormState {
  name: string;
  releaseDate: string;
  imgMode: 'file' | 'path';
  imgFile: File | null;
  imgPath: string;
  notes: string;
  isActive: boolean;
}

function expansionToForm(exp: Expansion): ExpFormState {
  return {
    name: exp.name,
    releaseDate: exp.release_date,
    imgMode: 'path',
    imgFile: null,
    imgPath: exp.expansion_path ?? '',
    notes: exp.notes ?? '',
    isActive: exp.is_active,
  };
}

function buildFormData(form: ExpFormState): FormData {
  const fd = new FormData();
  fd.append('name', form.name);
  fd.append('release_date', form.releaseDate);
  if (form.imgMode === 'file' && form.imgFile) fd.append('expansion_file', form.imgFile);
  else if (form.imgPath) fd.append('expansion_path', form.imgPath);
  if (form.notes) fd.append('notes', form.notes);
  fd.append('is_active', form.isActive ? '1' : '0');
  return fd;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface ExpansionAccordionProps {
  expansion: Expansion;
  onDetailUpdated: (updated: Expansion) => void;
}

const ExpansionAccordion: React.FC<ExpansionAccordionProps> = ({ expansion, onDetailUpdated }) => {
  const [activeTab, setActiveTab] = useState<'dati' | 'inserimento' | 'droprate'>('dati');

  // ── Tab Dati ──
  const [form, setForm] = useState<ExpFormState>(() => expansionToForm(expansion));
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // ── Tab Inserimento ──
  const [allCards, setAllCards] = useState<Card[] | null>(null);
  const [loadingAllCards, setLoadingAllCards] = useState(false);
  const [cardFilter, setCardFilter] = useState('');
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [pendingDropRate, setPendingDropRate] = useState('');
  const [isAttaching, setIsAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [detachingId, setDetachingId] = useState<number | null>(null);

  // ── Tab Drop Rate ──
  const [dropRateInputs, setDropRateInputs] = useState<Record<number, string>>(
    () => Object.fromEntries((expansion.cards ?? []).map((c) => [c.id, String(c.pivot.drop_rate)]))
  );
  const [updatingRateId, setUpdatingRateId] = useState<number | null>(null);
  const [rateErrors, setRateErrors] = useState<Record<number, string>>({});

  // Sync drop rate inputs when cards list changes
  useEffect(() => {
    setDropRateInputs(
      Object.fromEntries((expansion.cards ?? []).map((c) => [c.id, String(c.pivot.drop_rate)]))
    );
  }, [expansion.cards]);

  // Lazy fetch all cards when inserimento tab is opened
  useEffect(() => {
    if (activeTab === 'inserimento' && allCards === null && !loadingAllCards) {
      setLoadingAllCards(true);
      apiGetCards()
        .then((data) => setAllCards(Array.isArray(data) ? data : (data as any)?.data ?? []))
        .catch(() => setAllCards([]))
        .finally(() => setLoadingAllCards(false));
    }
  }, [activeTab, allCards, loadingAllCards]);

  const setField = (field: keyof ExpFormState) => (value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const attachedIds = new Set((expansion.cards ?? []).map((c) => c.id));
  const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';

  const toImgSrc = (path: string | null | undefined) =>
    path ? (path.startsWith('http') ? path : storageUrl + path) : null;

  // ── Handlers: Dati ──

  const handleSaveDati = async (e: FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    setSaveSuccess(false);
    setIsSaving(true);
    try {
      const fd = buildFormData(form);
      const response = (await apiUpdateExpansion(expansion.id, fd)) as any;
      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];
        const firstVal = response.errors[firstKey];
        setSaveError(String(Array.isArray(firstVal) ? firstVal[0] : (firstVal ?? 'Errore di validazione')));
        return;
      }
      setSaveSuccess(true);
      onDetailUpdated({ ...expansion, ...response, cards: expansion.cards });
    } catch {
      setSaveError('Errore durante il salvataggio. Riprova.');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Handlers: Inserimento ──

  const handleSelectCard = (id: number) => {
    if (attachedIds.has(id)) return;
    setSelectedCardId((prev) => (prev === id ? null : id));
    setPendingDropRate('');
    setAttachError(null);
  };

  const handleAttach = async () => {
    if (!selectedCardId) return;
    const dropRate = Number(pendingDropRate);
    if (!Number.isInteger(dropRate) || dropRate < 1 || dropRate > 10) {
      setAttachError('Inserisci un drop rate valido (intero da 1 a 10).');
      return;
    }
    setAttachError(null);
    setIsAttaching(true);
    try {
      await apiAttachCard(expansion.id, selectedCardId, dropRate);
      const updated = await apiGetExpansion(expansion.id);
      onDetailUpdated({ ...expansion, cards: updated.cards });
      setSelectedCardId(null);
      setPendingDropRate('');
    } catch {
      setAttachError("Errore durante l'associazione. Verifica che la carta non sia già associata.");
    } finally {
      setIsAttaching(false);
    }
  };

  const handleDetach = async (cardId: number) => {
    setDetachingId(cardId);
    setAttachError(null);
    try {
      await apiDetachCard(expansion.id, cardId);
      onDetailUpdated({
        ...expansion,
        cards: expansion.cards?.filter((c) => c.id !== cardId),
      });
      if (selectedCardId === cardId) setSelectedCardId(null);
    } catch {
      setAttachError('Errore durante la rimozione della carta.');
    } finally {
      setDetachingId(null);
    }
  };

  // ── Handlers: Drop Rate ──

  const handleUpdateDropRate = async (cardId: number) => {
    const newRate = Number(dropRateInputs[cardId]);
    if (!Number.isInteger(newRate) || newRate < 1 || newRate > 10) {
      setRateErrors((prev) => ({ ...prev, [cardId]: 'Intero da 1 a 10.' }));
      return;
    }
    setRateErrors((prev) => { const n = { ...prev }; delete n[cardId]; return n; });
    setUpdatingRateId(cardId);
    try {
      await apiUpdateCardDropRate(expansion.id, cardId, newRate);
      onDetailUpdated({
        ...expansion,
        cards: expansion.cards?.map((c) =>
          c.id === cardId ? { ...c, pivot: { ...c.pivot, drop_rate: newRate } } : c
        ),
      });
    } catch {
      setRateErrors((prev) => ({ ...prev, [cardId]: "Errore durante l'aggiornamento." }));
    } finally {
      setUpdatingRateId(null);
    }
  };

  const cards = expansion.cards ?? [];

  const filteredAllCards = (allCards ?? []).filter((c) => {
    if (!cardFilter) return true;
    const q = cardFilter.toLowerCase();
    if (String(c.id).includes(q)) return true;
    if (c.wrestlers?.some((w) => w.name.toLowerCase().includes(q))) return true;
    return false;
  });

  return (
    <Wrapper>
      <TabBar>
        <Tab active={activeTab === 'dati'} onClick={() => setActiveTab('dati')}>
          Dati
        </Tab>
        <Tab active={activeTab === 'inserimento'} onClick={() => setActiveTab('inserimento')}>
          Inserimento Carte
        </Tab>
        <Tab active={activeTab === 'droprate'} onClick={() => setActiveTab('droprate')}>
          Drop Rate{cards.length > 0 ? ` (${cards.length})` : ''}
        </Tab>
      </TabBar>

      {/* ── TAB: Dati ── */}
      {activeTab === 'dati' && (
        <TabContent>
          <form onSubmit={handleSaveDati}>
            <FormGroup>
              <Label>Nome</Label>
              <Input
                value={form.name}
                onChange={(e) => setField('name')(e.target.value)}
                required
                placeholder="es: Base Set 2024"
              />
            </FormGroup>

            <FormGroup>
              <Label>Data rilascio</Label>
              <Input
                type="date"
                value={form.releaseDate}
                onChange={(e) => setField('releaseDate')(e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Immagine</Label>
              <ToggleRow>
                <ToggleBtn type="button" active={form.imgMode === 'path'} onClick={() => setField('imgMode')('path')}>Path</ToggleBtn>
                <ToggleBtn type="button" active={form.imgMode === 'file'} onClick={() => setField('imgMode')('file')}>File</ToggleBtn>
              </ToggleRow>
              {form.imgMode === 'path' ? (
                <Input
                  value={form.imgPath}
                  onChange={(e) => setField('imgPath')(e.target.value)}
                  placeholder="es: expansions/cover.jpg"
                />
              ) : (
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setField('imgFile')(e.target.files?.[0] ?? null)}
                />
              )}
            </FormGroup>

            <FormGroup>
              <Label>Note</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setField('notes')(e.target.value)}
                placeholder="Note opzionali..."
              />
            </FormGroup>

            <FormGroup>
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setField('isActive')(e.target.checked)}
                />
                Espansione attiva
              </CheckboxRow>
            </FormGroup>

            {saveError && <ErrorMsg>{saveError}</ErrorMsg>}
            {saveSuccess && <SuccessMsg>Salvato con successo!</SuccessMsg>}

            <SaveBtn type="submit" disabled={isSaving}>
              {isSaving ? 'Salvataggio...' : 'Salva Dati'}
            </SaveBtn>
          </form>
        </TabContent>
      )}

      {/* ── TAB: Inserimento Carte ── */}
      {activeTab === 'inserimento' && (
        <TabContent>
          {attachError && <ErrorMsg style={{ marginBottom: 10 }}>{attachError}</ErrorMsg>}

          {loadingAllCards && <LoadingText>Caricamento carte...</LoadingText>}

          {!loadingAllCards && allCards !== null && (
            <>
              <FilterInput
                placeholder="Cerca per ID o nome wrestler..."
                value={cardFilter}
                onChange={(e) => setCardFilter(e.target.value)}
              />

              {filteredAllCards.length === 0 && <EmptyMsg>Nessuna carta trovata.</EmptyMsg>}

              <CardPickGrid>
                {filteredAllCards.map((card) => {
                  const isAttached = attachedIds.has(card.id);
                  const isSelected = selectedCardId === card.id;
                  const src = toImgSrc(card.fronte_path);

                  return (
                    <CardPickItem
                      key={card.id}
                      attached={isAttached}
                      selected={isSelected}
                      onClick={() => handleSelectCard(card.id)}
                      title={`#${card.id}${card.wrestlers?.map((w) => w.name).join(', ') ? ` — ${card.wrestlers.map((w) => w.name).join(', ')}` : ''}`}
                    >
                      {/* Immagine */}
                      <CardPickImgWrapper>
                        {src ? (
                          <CardPickImg src={src} alt={`Carta ${card.id}`} />
                        ) : (
                          <CardPickPlaceholder>#{card.id}</CardPickPlaceholder>
                        )}
                        {isAttached && (
                          <CardPickAttachedOverlay>✓</CardPickAttachedOverlay>
                        )}
                      </CardPickImgWrapper>

                      {/* ID label */}
                      <CardPickIdLabel>#{card.id}</CardPickIdLabel>

                      {/* Pannello espanso quando selezionata (non associata) */}
                      {isSelected && (
                        <CardPickExpandedPanel onClick={(e) => e.stopPropagation()}>
                          <DropRateLabel>Drop Rate %</DropRateLabel>
                          <DropRatePickInput
                            type="number"
                            min="1"
                            max="10"
                            step="1"
                            placeholder="es: 10"
                            value={pendingDropRate}
                            onChange={(e) => setPendingDropRate(e.target.value)}
                            disabled={isAttaching}
                            autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') handleAttach(); }}
                          />
                          <AddCardBtn onClick={handleAttach} disabled={isAttaching}>
                            {isAttaching ? 'Aggiungendo...' : '+ Aggiungi'}
                          </AddCardBtn>
                        </CardPickExpandedPanel>
                      )}

                      {/* Bottone rimuovi — visibile subito per le carte già associate */}
                      {isAttached && (
                        <RemoveCardBtn
                          onClick={(e) => { e.stopPropagation(); handleDetach(card.id); }}
                          disabled={detachingId === card.id}
                        >
                          {detachingId === card.id ? 'Rimozione...' : '✕ Rimuovi'}
                        </RemoveCardBtn>
                      )}
                    </CardPickItem>
                  );
                })}
              </CardPickGrid>
            </>
          )}
        </TabContent>
      )}

      {/* ── TAB: Drop Rate ── */}
      {activeTab === 'droprate' && (
        <TabContent>
          {cards.length === 0 && (
            <EmptyMsg>Nessuna carta associata. Aggiungile prima dalla scheda "Inserimento Carte".</EmptyMsg>
          )}

          <DropRateGrid>
            {cards.map((card: ExpansionCardItem) => {
              const src = toImgSrc(card.fronte_path);
              const wrestlerNames = card.wrestlers?.map((w) => w.name).join(', ');
              return (
                <DropRateCard key={card.id}>
                  <DropRateCardImgWrapper>
                    {src ? (
                      <DropRateCardImg src={src} alt={`Carta ${card.id}`} />
                    ) : (
                      <DropRateCardImgPlaceholder>Nessuna immagine</DropRateCardImgPlaceholder>
                    )}
                  </DropRateCardImgWrapper>
                  <DropRateCardBody>
                    <DropRateCardId>
                      #{card.id}{wrestlerNames ? ` — ${wrestlerNames}` : ''}
                    </DropRateCardId>
                    <DropRateCardControls>
                      <DropRateInput
                        type="number"
                        min="1"
                        max="10"
                        step="1"
                        value={dropRateInputs[card.id] ?? card.pivot.drop_rate}
                        onChange={(e) =>
                          setDropRateInputs((prev) => ({ ...prev, [card.id]: e.target.value }))
                        }
                        disabled={updatingRateId === card.id}
                      />
                      <UpdateBtn
                        onClick={() => handleUpdateDropRate(card.id)}
                        disabled={updatingRateId === card.id}
                      >
                        {updatingRateId === card.id ? '...' : 'Salva'}
                      </UpdateBtn>
                    </DropRateCardControls>
                    {rateErrors[card.id] && <InlineError>{rateErrors[card.id]}</InlineError>}
                  </DropRateCardBody>
                </DropRateCard>
              );
            })}
          </DropRateGrid>
        </TabContent>
      )}
    </Wrapper>
  );
};

export default ExpansionAccordion;
